import { useState, useCallback } from "react";
import { API_CONFIG } from "../config/apiConfig";
import { apiClient } from "../utils/apiClient";

interface RepairEligibilityResponse {
  success: boolean;
  eligible: boolean;
  count: number;
  error?: string;
}

interface UseIsRepairEligibleProps {
  isLoading: boolean;
  error: string | null;
  checkEligibility: (whid: string, itemNumber: string) => Promise<boolean>;
}

export const useIsRepairEligible = (): UseIsRepairEligibleProps => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkEligibility = useCallback(
    async (whid: string, itemNumber: string): Promise<boolean> => {
      if (!whid || !itemNumber) {
        setError("WHID and Item Number are required");
        return false;
      }

      setIsLoading(true);
      setError(null);

      try {
        const { data: responseData } = await apiClient.get<RepairEligibilityResponse>(
          API_CONFIG.ENDPOINTS.IS_REPAIR_ELIGIBLE,
          {
            params: {
              p_whid: String(whid).trim(),
              p_item_number: String(itemNumber).trim(),
            },
          }
        );

        if (responseData.success) {
          return responseData.eligible;
        } else {
          setError(responseData.error || "Failed to check repair eligibility");
          return false;
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || error.message || "Failed to check repair eligibility";
        setError(errorMessage);
        console.error("Repair Eligibility Error:", errorMessage);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { isLoading, error, checkEligibility };
};